import { useCallback } from 'react'
import log from 'loglevel'
import { ControlServicePromiseClient } from '@trustero/trustero-api-web/lib/model/control_grpc_web_pb'
import {
  Control,
  DismissControlRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { useShowConfirmationError } from 'src/Confirmation/ConfirmationContext'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { useAuthorizedGrpcClient } from '../../../../adapter'
import { useCurrentAudit } from '../audit'
import { useRefreshControlPolicyLinks } from './useControlMutations'

/**
 * useDeleteControl deletes the control, with its links to audits, policies and the
 * rest. A failure shows in the confirmation dialog it is called from.
 */
export const useDeleteControl = ({
  control: { id },
}: {
  control: Control.AsObject
}): (() => Promise<void>) => {
  const refreshControlPolicyLinks = useRefreshControlPolicyLinks()
  const { data: currentAudit, mutate: mutateAudit } = useCurrentAudit()
  const controlClient = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const showError = useShowConfirmationError()

  return useCallback(async () => {
    try {
      await controlClient.dismissControl(
        new DismissControlRequest().setControlId(id),
      )
    } catch (error) {
      const { message, shouldLog } = getErrorMessage(error)
      if (shouldLog) log.error('Error deleting control', error)
      showError('Unable to Delete Control', message)
      return
    }
    try {
      await refreshControlPolicyLinks()
      if (currentAudit) await mutateAudit()
    } catch (error) {
      log.error('Error refreshing after deleting a control', error)
    }
  }, [
    controlClient,
    id,
    showError,
    refreshControlPolicyLinks,
    currentAudit,
    mutateAudit,
  ])
}
